import { useState } from "react";
import { motion } from "framer-motion";
import { Play, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import heroMockup from "@/assets/new-landing/hero-mockup.jpg";

const VideoShowcase = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 },
    },
  };

  return (
    <>
      <motion.section
        className="min-h-screen flex items-center justify-center px-6 py-24 bg-surface/30 relative overflow-hidden"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        {/* Background gradient orbs */}
        <div className="absolute top-1/4 left-[5%] w-[400px] h-[400px] rounded-full bg-accent/5 blur-[120px] -z-5" />
        <div className="absolute bottom-1/4 right-[5%] w-[350px] h-[350px] rounded-full bg-accent/8 blur-[100px] -z-5" />

        <div className="container mx-auto max-w-5xl relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <motion.h2
              className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-foreground mb-6"
              variants={fadeInUp}
            >
              {t("marketing.video.title", {
                defaultValue: "See FanJago in Action",
              })}
            </motion.h2>
            <motion.p
              className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto"
              variants={fadeInUp}
            >
              {t("marketing.video.subtitle", {
                defaultValue:
                  "A quick look at how clubs, coaches, parents, players, and fans use one platform.",
              })}
            </motion.p>
          </div>

          {/* Video Thumbnail */}
          <motion.div
            className="relative aspect-video max-w-4xl mx-auto rounded-2xl overflow-hidden cursor-pointer group"
            variants={fadeInUp}
            onClick={() => setIsOpen(true)}
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            {/* Thumbnail Image */}
            <img
              src={heroMockup}
              alt={t("marketing.video.thumbnailAlt", {
                defaultValue: "FanJago product preview",
              })}
              className="w-full h-full object-cover"
            />

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />

            {/* Play Button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                className="relative"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                {/* Pulsing glow effect */}
                <motion.div
                  className="absolute inset-0 bg-accent rounded-full blur-xl opacity-50"
                  animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.3, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
                
                {/* Play button */}
                <div className="relative w-20 h-20 md:w-24 md:h-24 bg-accent rounded-full flex items-center justify-center shadow-xl shadow-accent/30 group-hover:bg-accent-soft transition-colors">
                  <Play className="w-8 h-8 md:w-10 md:h-10 text-accent-foreground ml-1" fill="currentColor" />
                </div>
              </motion.div>
            </div>

            {/* Duration Badge */}
            <div className="absolute bottom-4 right-4 bg-background/80 backdrop-blur-sm px-3 py-1.5 rounded-lg">
              <span className="text-sm font-medium text-foreground">
                {t("marketing.video.duration", {
                  defaultValue: "2:30",
                })}
              </span>
            </div>

            {/* Watch Demo Label */}
            <div className="absolute bottom-4 left-4">
              <span className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Play className="w-5 h-5" />
                {t("marketing.video.watchNow", {
                  defaultValue: "Watch now",
                })}
              </span>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Video Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-5xl w-full p-0 bg-background border-border overflow-hidden">
          <DialogTitle className="sr-only">
            {t("marketing.video.title", {
              defaultValue: "See FanJago in Action",
            })}
          </DialogTitle>
          
          {/* Close Button */}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-muted transition-colors"
            aria-label={t("marketing.video.close", { defaultValue: "Close" })}
          >
            <X className="w-5 h-5 text-foreground" />
          </button>

          {/* Video Player */}
          <div className="aspect-video w-full bg-muted">
            {/* Placeholder for video - replace with actual video embed */}
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center p-8">
                <Play className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg text-muted-foreground">
                  {t("marketing.video.placeholder", {
                    defaultValue: "Video coming soon",
                  })}
                </p>
                <p className="text-sm text-muted-foreground/70 mt-2">
                  {t("marketing.video.placeholderHint", {
                    defaultValue: "Replace this placeholder with your video embed.",
                  })}
                </p>
              </div>
            </div>
            
            {/* When you have a video URL, replace the above with:
            <iframe
              src="https://www.youtube.com/embed/VIDEO_ID?autoplay=1"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            */}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VideoShowcase;
